import { Box, Divider, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles'
import React, { useContext, useState } from 'react'
import Carousel, { CarouselElement } from './components/carousel/carousel'
import SignUp, { SignUpCallbackType, SignUpProps } from './components/sign-up/sign-up'
import styles from './grieg-onboarding.module.scss'
import { Mobile, Email } from '@griegconnect/krakentools-react-icons'

export type PasswordlessSignInType = 'sms' | 'email'
export type SignInType = 'username-password' | PasswordlessSignInType

export type GriegOnboardingProps = Pick<SignUpProps, 'flow' | 'accountExists'> & {
  carouselElements: CarouselElement[]
  /**
   * This option should only be used if you need to override the defaults
   */
  signInMethods?: SignInType[]
  children: React.ReactNode
  disableCreateAccount?: boolean
  forceSignup?: boolean
  initVisible?: boolean
  loginRedirectOnSingleSignInMethod?: boolean
  /**
   * Only use if you want to override the default flow.
   */
  onClose?: () => Promise<void> | void
  onLogin?: () => Promise<void>
  onPasswordlessLogin?: (type: PasswordlessSignInType) => Promise<void>
  passwordlessDescription?: string
  onSignUp: SignUpCallbackType
  privacyPolicyUrl: string
  signUpFields: string[]
  termsOfServiceUrl: string
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#65bbff',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
})

export type Onboarding = {
  setOnboardingVisibility: (display: boolean) => void
  displayOnboarding: boolean
}

export const GriegOnboardingContext = React.createContext<Onboarding | null>(null)

export const useOnboarding = () => useContext(GriegOnboardingContext)!

export const GriegOnboardingProvider = (props: GriegOnboardingProps) => {
  const {
    carouselElements,
    termsOfServiceUrl,
    privacyPolicyUrl,
    onPasswordlessLogin,
    onLogin,
    onSignUp,
    signUpFields,
    children,
  } = props
  const [displayOnboarding, setDisplayOnboarding] = useState(props.initVisible ?? false)
  const [showSignUp, setShowSignUp] = useState(props.forceSignup ?? false)

  const closeSignUp = () => {
    if (props.onClose) {
      props.onClose()
    } else {
      setShowSignUp(false)
    }
  }

  const setOnboardingVisibility = (display: boolean) => {
    setDisplayOnboarding(display)
  }

  return (
    <GriegOnboardingContext.Provider
      value={{
        setOnboardingVisibility,
        displayOnboarding,
      }}
    >
      {displayOnboarding === true && (
        <ThemeProvider theme={theme}>
          <div className={styles.onboardingWrapper} id="onboarding">
            {carouselElements.length > 0 && <Carousel elements={carouselElements} />}
            <div className={styles.actionWrapper}>
              <>
                <div style={{ marginBottom: 8 }}>
                  {onLogin && (
                    <UsernamePasswordLogin
                      onLogin={onLogin}
                      disableCreateAccount={props.disableCreateAccount}
                      onShowSignup={() => setShowSignUp(true)}
                    />
                  )}
                  {onLogin && onPasswordlessLogin && <LoginDivider />}
                  {onPasswordlessLogin &&
                    ((props.signInMethods && props.signInMethods.includes('sms')) ||
                      (props.signInMethods && props.signInMethods.includes('email'))) && (
                      <PasswordlessLogin
                        types={
                          props.signInMethods.filter((type) =>
                            ['sms', 'email'].includes(type)
                          ) as PasswordlessSignInType[]
                        }
                        onLogin={onPasswordlessLogin}
                        description={props.passwordlessDescription}
                      />
                    )}
                </div>
                {onLogin && (
                  <>
                    {props.disableCreateAccount ? (
                      <>
                        {onPasswordlessLogin && <Divider className={styles.divider} />}
                        <Typography variant="body2" className={styles.hint}>
                          Do you need access?
                          <br /> Please contact your administrator.
                        </Typography>
                      </>
                    ) : props.onPasswordlessLogin === undefined ? (
                      <Typography variant="body2" className={styles.hint}>
                        To continue create an account (Grieg ID) or sign in.
                      </Typography>
                    ) : null}
                  </>
                )}
              </>
            </div>
            <SignUp
              display={showSignUp}
              onClose={closeSignUp}
              termsOfServiceUrl={termsOfServiceUrl}
              privacyPolicyUrl={privacyPolicyUrl}
              signUpFields={signUpFields}
              onSignUp={onSignUp}
              flow={props.flow}
              accountExists={props.accountExists}
            />
          </div>
        </ThemeProvider>
      )}
      {displayOnboarding === false && children}
    </GriegOnboardingContext.Provider>
  )
}

const UsernamePasswordLogin = (props: {
  onLogin: NonNullable<GriegOnboardingProps['onLogin']>
  disableCreateAccount: GriegOnboardingProps['disableCreateAccount']
  onShowSignup: () => void
}) => {
  return (
    <>
      {props.disableCreateAccount ? (
        <Button onClick={() => props.onLogin()} variant="outlined" color="primary">
          Sign in with Grieg ID
        </Button>
      ) : (
        <>
          <Button onClick={() => props.onShowSignup()} variant="outlined" color="primary" sx={{ ml: 1, mr: 1 }}>
            Create account
          </Button>
          <Button id="sign-in" color="primary" variant="outlined" onClick={() => props.onLogin()} sx={{ ml: 1, mr: 1 }}>
            Sign in
          </Button>
        </>
      )}
    </>
  )
}

const PasswordlessLogin = (props: {
  types: PasswordlessSignInType[]
  onLogin: (type: PasswordlessSignInType) => void
  description: GriegOnboardingProps['passwordlessDescription']
}) => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: () => ({ xs: 1.5, sm: 0 }),
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: () => ({ xs: 'column', sm: 'row' }),
        }}
      >
        {props.types.map((type) => (
          <Button
            key={type}
            id="sign-in"
            color="primary"
            variant="outlined"
            onClick={() => props.onLogin(type)}
            sx={{ ml: 1, mr: 1 }}
            startIcon={type === 'sms' ? <Mobile /> : <Email />}
          >
            {type === 'sms' ? 'Sign in with phone' : 'Sign in with email'}
          </Button>
        ))}
      </Box>
      {props.description && (
        <Typography variant="body2" className={styles.hint} sx={{ mt: 1 }}>
          {props.description}
        </Typography>
      )}
    </>
  )
}

const LoginDivider = () => {
  return (
    <div>
      <div
        style={{
          display: 'inline-block',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginBottom: 6,
        }}
      >
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <Divider sx={{ minWidth: 62 }} />
          <Box>
            <Typography component="span" sx={{ color: 'text.disabled', fontSize: 12 }}>
              OR
            </Typography>
          </Box>
          <Divider sx={{ minWidth: 62 }} />
        </Stack>
      </div>
    </div>
  )
}
